import { JSX, createSignal, createEffect, onCleanup } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { SendOTPResult } from "~/data/auth";

export interface OtpModalProps {
  phoneNumber: string;
  onSubmit: (otp: string) => Promise<void>;
  onResend: () => Promise<SendOTPResult>;
  onBack: () => void;
  onClose: () => void;
}

export function OtpModal(props: OtpModalProps): JSX.Element {
  const { components } = useTelescopeContext();

  const [otp, setOtp] = createSignal<string>("");
  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [isResending, setIsResending] = createSignal<boolean>(false);
  const [error, setError] = createSignal<string | undefined>(undefined);
  const [resendCountdown, setResendCountdown] = createSignal<number>(30);

  let timer: ReturnType<typeof setInterval> | undefined;

  const startCountdown = () => {
    setResendCountdown(30);
    if (timer) clearInterval(timer);

    timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev === 1) {
          if (timer) clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  createEffect(() => {
    startCountdown();
  });

  onCleanup(() => {
    if (timer) clearInterval(timer);
  });

  // Auto-submit when 6 digits entered
  createEffect(() => {
    if (otp().length === 6 && !isLoading() && !error()) {
      handleSubmit();
    }
  });

  const handleSubmit = async () => {
    if (isLoading() || otp().length !== 6) return;

    setIsLoading(true);
    setError(undefined);
    try {
      await props.onSubmit(otp());
    } catch (err: any) {
      setError(err?.message ?? "Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (isResending() || resendCountdown() > 0) return;

    setIsResending(true);
    setOtp("");
    setError(undefined);
    try {
      await props.onResend();
      startCountdown();
    } catch (err: any) {
      setError(err?.message ?? "Failed to resend OTP");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div class="flex w-full flex-col ">
      <div class="relative flex items-center justify-center px-5 pb-4 pt-6">
        {/* Back button */}
        <button
          onClick={props.onBack}
          class="absolute left-4 top-4 flex size-6 items-center justify-center rounded-full"
        >
          <BackArrowIcon />
        </button>

        {/* Close button */}
        <button
          onClick={props.onClose}
          class="absolute right-4 top-4 flex size-6 items-center justify-center rounded-full"
        >
          <CloseIcon />
        </button>

        {/* Header content */}
        <div class="flex flex-col items-center gap-1 text-center">
          <h2 class="text-title-4-semi-bold text-text-normal-primary">
            Enter OTP
          </h2>
          <p class="text-para-3-regular text-text-normal-secondary">
            Sent to {props.phoneNumber}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-4 px-5 pb-5">
        <components.OtpInput
          otp={otp}
          setOtp={setOtp}
          isError={!!error()}
          removeFocus={isLoading()}
        />

        {error() && (
          <p class="text-center text-label-regular text-error-base">
            {error()}
          </p>
        )}

        {resendCountdown() > 0 ? (
          <p class="text-center text-para-3-regular text-text-normal-secondary">
            Resend in {resendCountdown()}
          </p>
        ) : (
          <button
            onClick={handleResend}
            disabled={isResending()}
            class="text-center text-para-3-medium text-text-normal-primary underline underline-offset-2 disabled:opacity-50"
          >
            {isResending() ? "Sending..." : "Didn't get OTP? Resend OTP"}
          </button>
        )}
      </div>

      <div class="border-t border-stroke-1 px-5 py-4">
        <components.Button
          text="Continue"
          onClick={handleSubmit}
          variant="primary"
          style="neutral"
          size={40}
          expanded={true}
          loading={isLoading()}
          disabled={isLoading() || otp().length !== 6}
        />
      </div>
    </div>
  );
}

function BackArrowIcon(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.8333 10H4.16667M4.16667 10L10 15.8333M4.16667 10L10 4.16667"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-text-normal-primary"
      />
    </svg>
  );
}

function CloseIcon(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15 5L5 15M5 5L15 15"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-text-normal-primary"
      />
    </svg>
  );
}
